import { renderMetaTags, TitleMetaLinkTag, useQuerySubscription } from "react-datocms";
import { GetStaticProps } from "next";
import { Api } from "@/api";
import { Layout, SEO } from "@/components";
import { SimpleHero } from "@/components/SimpleHero";
import { FeaturesSection } from "@/components/home/FeaturesSection";
import { IconFeatures3up } from "@/components/shared/IconFeatures3up";
import { DATOCMS_API_TOKEN, EXTERNAL } from "@/config";
import {
  accordionFragment,
  AccordionFragmentType,
  metaTagsFragment,
  responsiveImageFragment,
  Accordion,
} from "@/modules";
import { ImageType, Subscription } from "@/types";

export interface IReferralsPage {
  heading: string;
  subheading: string;
  heroBackgroundImage: ImageType;
  ctaText: string;
  featuresHeading: string;
  referralFeatures: { id: string; title: string; description: string; icon: ImageType }[];
  benefitsHeading: string;
  benefits: { id: string; title: string; description: string; image: ImageType; ctaText: string }[];
  referralsFaqs: AccordionFragmentType;
  seo: TitleMetaLinkTag[];
}

type HomePageQueryResponse = {
  page: IReferralsPage;
};

type Props = {
  preview: boolean;
  subscription: Subscription<HomePageQueryResponse>;
};

const ReferralsPage: React.FC<Props> = ({ preview, subscription }) => {
  const { data: { page: data } = {} } = useQuerySubscription(subscription);
  if (!data) return null;

  const {
    seo,
    heading,
    subheading,
    heroBackgroundImage,
    ctaText,
    referralsFaqs,
    featuresHeading,
    benefits,
    referralFeatures,
  } = data;

  return (
    <Layout preview={preview} className="justify-center">
      <SEO
        title="Narrative | Image selection | Blogging software | Photographers"
        imageUrl="/images/share-image-select.jpg"
      >
        {renderMetaTags(seo)}
      </SEO>
      <SimpleHero
        title={heading}
        subtitle={subheading}
        heroBackgroundImage={heroBackgroundImage}
        ctaText={ctaText}
        ctaHref={EXTERNAL.REFERRALS.HREF}
      />
      <IconFeatures3up title={featuresHeading} items={referralFeatures} />
      <FeaturesSection
        title={featuresHeading}
        sections={benefits.map((benefit) => ({ ...benefit, ctaHref: EXTERNAL.REFERRALS.HREF }))}
      />
      <Accordion className="bg-white" {...referralsFaqs} />
    </Layout>
  );
};

/**
 * Static props
 */
export const getStaticProps: GetStaticProps<Props> = async ({ preview = false }) => {
  const graphqlRequest = {
    preview,
    query: `
      {
        page: pageReferral {
          heading
          subheading
          heroBackgroundImage {
            responsiveImage(imgixParams: { auto: format, w: 1920 }) {
              ...responsiveImageFragment
            }
          }
          ctaText
          featuresHeading
          referralFeatures {
            id
            title
            description
            icon {
              responsiveImage(imgixParams: { auto: format, w: 75, h: 75 }) {
                ...responsiveImageFragment
              }
            }
          }
          benefitsHeading
          benefits {
            id
            title
            description
            ctaText
            image {
              responsiveImage(imgixParams: { auto: format, w: 586 }) {
                ...responsiveImageFragment
              }
            } 
          }
          referralsFaqs {
            ...accordionFragment
          }
          seo: _seoMetaTags {
            ...metaTagsFragment
          }
        }
      }
      ${accordionFragment}
      ${metaTagsFragment}
      ${responsiveImageFragment}
    `,
  };

  const {
    data: { data: initialData },
  } = await Api.datocmsGraphqlRequest<HomePageQueryResponse>(graphqlRequest);

  return {
    props: {
      preview,
      subscription: preview
        ? {
            ...graphqlRequest,
            initialData,
            token: DATOCMS_API_TOKEN,
          }
        : {
            initialData,
            enabled: false,
          },
    },
  };
};

export default ReferralsPage;
